import { useRef, useEffect, useMemo, useState } from 'react';
import { NextSeo, ProductJsonLd, FAQPageJsonLd, BreadcrumbJsonLd } from 'next-seo';
import { ApiRoutes, AppRoutes } from 'src/lib/routes';
import OtherOptions from 'src/components/OtherOptions';
import { InferGetStaticPropsType, GetStaticProps, GetStaticPaths } from 'next';
import RevalidateSettings from 'src/lib/revalidate_settings';
import { Image, Category, Collection, SimpleCollection, TravelItinerary, Pagination, Blog } from 'src/types';
import ApiClient from 'src/lib/api_client';
import Breadcrumb from 'src/components/Breadcrumb';
import BreadcrumbItem from 'src/components/BreadcrumbItem';
import Link from 'next/link';
import WithDefaultJsonLd from 'src/components/WithDefaultJsonLd';
import useFilter from 'src/hooks/useFilter';
import { DefaultCollectionHeroImage, HEADER_HEIGHT } from 'src/lib/constants';
import CollectionHorizontalCard from 'src/components/CollectionHorizontalCard';
import useBreakpoints from 'src/hooks/useBreakpoints';
import Slider from 'react-slick';
import { commonCarouselSettings } from 'src/lib/constants';
import ImageWithFallback from 'src/components/ImageWithFallback';
import imageStyle from 'src/styles/modules/components/image.module.scss';
import Homework from 'src/components/Homework';
import { sortBy, truncate } from 'lodash-es';
import useInitialData from 'src/hooks/useInitialData';
import CountryCityPageNavV2 from 'src/components/CountryCityPageNavV2';
import useQuery from 'src/hooks/useQuery';
import { useTranslations } from 'next-intl';
import { prepareTranslations } from 'src/lib/prepare_translations';
import useAddLocaleToUrl from 'src/hooks/useAddLocaleToUrl';
import RelatedCollections from 'src/components/collection/RelatedCollections';

const Page: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = ({
  category,
  collection,
}) => {
  const addLocaleToUrl = useAddLocaleToUrl()
  const translation = useTranslations('pages/collections/[id]/[cateId]')
  const { isScreenMd } = useBreakpoints();
  const initialData = useInitialData();
  const containerOptionsRef = useRef<HTMLDivElement>(null)
  const { setFilterRecord } = useFilter();
  const image: Image | undefined = category.images[0] || collection.image || DefaultCollectionHeroImage;
  const images = category.images.length >= 3 ? category.images : category.images.concat(
    collection.images && collection.images.length > 0
      ? collection.images
      : []
  );

  const countryOrCityRecord = collection.cityId
    ? initialData.cities.find((o) => o.id === collection.cityId)
    : collection.countryId
    ? initialData.countries.find((o) => o.id === collection.countryId)
    : undefined;

  const { data: travelItineraryData } = useQuery<{
    data: TravelItinerary[];
    pagination: Pagination;
  }>(
    ApiRoutes.apiTravelItinerariesRoute(
      {},
      {
        q: {
          g: {
            0: {
              status_eq: 'published',
            },
            1: {
              g: {
                0: {
                  country_id_eq: collection.countryId,
                },
                1: {
                  travel_itinerary_cities_city_country_id_eq: collection.countryId,
                },
                2: {
                  travel_itinerary_cities_city_id_in: collection.cityId ? [collection.cityId] : [],
                },
              },
              m: 'or',
            },
          },
          s: 'views_count desc',
        },
        page: 1,
        per_page: 20,
      }
    ).toUrl()
  );

  const { data: blogsData } = useQuery<{ data: Blog[]; pagination: Pagination }>(
    ApiRoutes.apiBlogsRoute(
      {},
      {
        collection_ids: [collection.id],
        sort: 'most_popular',
        page: 1,
        per_page: 20,
      }
    ).toUrl()
  );

  const cities = useMemo(
    () =>
      sortBy(
        initialData.cities.filter((o) => o.countryId === collection.countryId),
        [(o) => (o.featured ? 0 : 1), (o) => -o.optionsCount]
      ),
    [collection.id, initialData.cities.length]
  );

  const { data: itineraries = [] } = travelItineraryData || {};
  const { data: blogs = [] } = blogsData || {};
  const sortedBlogs = sortBy(blogs, (o) =>
    o.linkableType === 'Collection' && Number(o.linkableId) === collection.id ? 0 : 1
  );

  const otherCategories = useMemo(
    () =>
      (collection.categories || [])
        .filter((o) => !o.collectionMostPopular && o.optionsCount > 0)
        .map((o) =>
          o.id === category.id
            ? null
            : {
                id: o.id,
                url: AppRoutes.cateCollectionRoute({
                  id: collection.slugCode,
                  cateId: o.slug,
                }).toUrl(),
                name: o.name,
                strippedDescription: o.description,
                image: o.image || collection.image || DefaultCollectionHeroImage,
              }
        )
        .filter((o) => o) as SimpleCollection[],
    [category.id]
  );
  const otherRecords = useMemo(() => [collection, ...otherCategories], [category.id]);
  const metaTitle = category.metaTitle ? category.metaTitle : `${category.name} ${translation('for')} ${collection.name}`;
  const metaDescription = category.metaDescription ? category.metaDescription : category.description;

  useEffect(() => {
    if (category.id) {
      setFilterRecord({
        id: category.id,
        type: 'Category',
        name: category.name,
        prevPath: AppRoutes.cateCollectionRoute({ id: collection.slugCode, cateId: category.slug }).toPath()
      });
    }
  }, [category.id]);

  const breadcrumbs = () => {
    const items = []
    if (collection.cityId) {
      if (collection.citySlug !== 'singapore') {
        items.push({
          item: addLocaleToUrl(AppRoutes.countryRoute({ id: collection.countrySlug as string }).toUrl()),
          name: collection.countryName,
        });
      }
      items.push({
        item: addLocaleToUrl(AppRoutes.cityRoute({ id: collection.citySlug as string }).toUrl()),
        name: collection.cityName,
      });
    } else if (collection.countryId) {
      items.push({
        item: collection.countrySlug === 'singapore'
          ? addLocaleToUrl(AppRoutes.cityRoute({ id: collection.countrySlug as string }).toUrl() )
          : addLocaleToUrl(AppRoutes.countryRoute({ id: collection.countrySlug as string }).toUrl()),
        name: collection.countryName,
      });
    }

    items.push({
      item: addLocaleToUrl(AppRoutes.collectionRoute({ id: collection.slugCode }).toUrl()),
      name: collection.name,
    });

    items.push({
      item: addLocaleToUrl(AppRoutes.cateCollectionRoute({ id: collection.slugCode, cateId: category.slug }).toUrl()),
      name: category.name,
    });

    return items.map((item, index) => ({ ...item, position: index + 1 }));
  }

  return (
    <>
      <NextSeo
        title={metaTitle}
        description={metaDescription}
        canonical={addLocaleToUrl(AppRoutes.cateCollectionRoute({ id: collection.slugCode, cateId: category.slug }).toUrl())}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          images: image?.mdUrl
            ? [
                {
                  url: image.mdUrl,
                  width: 600,
                  height: 600,
                  alt: metaTitle,
                },
              ]
            : undefined,
        }}
      />
      <WithDefaultJsonLd>
        <BreadcrumbJsonLd itemListElements={breadcrumbs()} />
        {collection.ratedReviewsCount && collection.ratedReviewsCount > 0 ? (
          <ProductJsonLd
            productName={collection.name}
            description={collection.description}
            images={image?.mdUrl ? [image.mdUrl] : []}
            aggregateRating={{
                ratingValue: collection.reviewRating,
                reviewCount: collection.ratedReviewsCount,
            }}
          />
        ) : null}

        {collection.nextSeoFaqs ? <FAQPageJsonLd mainEntity={collection.nextSeoFaqs} /> : null}
      </WithDefaultJsonLd>

      {countryOrCityRecord ? (
        <CountryCityPageNavV2 fieldGuide={collection.fieldGuide} location={collection.location} />
      ) : null}

      <div className="tw-bg-brand-yellow tw-py-16 md:tw-py-24">
        <div className="container">
          <Breadcrumb className="breadcrumb--black tw-justify-center tw-mb-4 md:tw-mb-0 tw-pt-0 tw-text-xs md:tw-text-sm">
            {collection.cityId ? (
              <>
                {collection.citySlug !== 'singapore' ? (
                  <BreadcrumbItem className="tw-hidden md:tw-block">
                    <Link legacyBehavior prefetch={false}                       href={AppRoutes.countryRoute({
                        id: collection.countrySlug as string,
                      }).toPath()}
                    >
                      <a>{collection.countryName}</a>
                    </Link>
                  </BreadcrumbItem>
                ) : null}
                <BreadcrumbItem className="tw-hidden md:tw-block">
                  <Link legacyBehavior prefetch={false} href={AppRoutes.cityRoute({ id: collection.citySlug as string }).toPath()}>
                    <a>{collection.cityName}</a>
                  </Link>
                </BreadcrumbItem>
              </>
            ) : collection.countryId ? (
              <BreadcrumbItem className="tw-hidden md:tw-block">
                {collection.countrySlug === 'singapore' ? (
                  <a href={AppRoutes.cityRoute({ id: collection.countrySlug }).toPath()}>
                    {collection.countryName}
                  </a>
                ) : (
                  <Link legacyBehavior prefetch={false} href={AppRoutes.countryRoute({ id: collection.countrySlug as string }).toPath()}>
                    <a>{collection.countryName}</a>
                  </Link>
                )}
              </BreadcrumbItem>
            ) : null}
            <BreadcrumbItem>
              <Link legacyBehavior prefetch={false} href={AppRoutes.collectionRoute({ id: collection.slugCode }).toPath()}>
                <a>{collection.name}</a>
              </Link>
            </BreadcrumbItem>
          </Breadcrumb>
        </div>

        <div className="container tw-max-w-2xl tw-mt-8 tw-mb-8 md:tw-mb-16">
          <h1 className="tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none tw-text-center tw-text-4xl md:tw-text-6xl">
            {category.name}
          </h1>
        </div>

        <div>
          <Slider
          {...commonCarouselSettings}
          slidesToShow={1}
          slidesToScroll={1}
          centerMode={true}
          centerPadding="500px"
          infinite={true}
          arrows={true}
          className="carousel-collection-images"
          responsive={[
            {
              breakpoint: 1500,
              settings: {
                centerPadding: '325px',
              },
            },
            {
              breakpoint: 1040,
              settings: {
                centerPadding: '200px'
              },
            },
            {
              breakpoint: 800,
              settings: {
                centerPadding: '0px',
                arrows: false,
                dots: true
              },
            },
          ]}
        >
          {images.slice(0, isScreenMd ? undefined : 5).map((image, index) => (
            <div key={index} className="md:tw-px-2">
              <ImageWithFallback
                record={image}
                imgField="webpLgUrl"
                smImgField="webpMdUrl"
                alt={image.caption || ''}
                className={`tw-object-cover tw-block tw-static md:tw-rounded-lg ${imageStyle.collectionHeroImage}`}
                fill
              />
            </div>
          ))}
          </Slider>
          <div className="tw-relative tw-bg-brand-yellow md:tw-rounded-none md:tw-mt-0 tw--mt-2 tw-rounded-tl-lg tw-rounded-tr-lg">
            <div className="tw-pt-18 tw-text-center tw-w-72 tw-px-1 md:tw-max-w-md tw-mx-auto">
              <div>{category.description}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="tw-py-16 md:tw-py-24">
        <div ref={containerOptionsRef}></div>
        <OtherOptions
          hideTitle={true}
          isGiftExperience={!!collection.giftCountryId}
          record={{
            id: category.id,
            type: 'Category',
          }}
          initialTotalCount={collection.optionsCount}
        />
      </div>

      <div className="tw-bg-purple tw-py-16 md:tw-py-24">
        <div className="container tw-mb-8 md:tw-mb-16">
          <h2 className="tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none tw-text-center tw-text-4xl">
            {translation('moreFormThisCollection')}
          </h2>
        </div>

        <div className="container">
          <div className="row tw-hidden md:tw-flex">
            {otherRecords.slice(0, 4).map((collection) => (
              <div key={collection.id} className="col-6 tw-mb-8">
                <CollectionHorizontalCard
                  collection={collection}
                  title={collection.name}
                  descLength={100}
                />
              </div>
            ))}
          </div>

          <div className="tw-block md:tw-hidden">
            <Slider
              {...commonCarouselSettings}
              slidesToScroll={1}
              slidesToShow={1}
              arrows={false}
              dots={true}
              className="slider-v2 slider-v2-green-dots carousel-homework tw--mx-2"
            >
              {otherRecords.slice(0, 4).map((collection) => (
                <div key={collection.id} className="tw-mb-8 tw-px-2 tw-outline-none">
                  <Link legacyBehavior prefetch={false} href={'slugCode' in collection ? AppRoutes.collectionRoute({ id: collection.slugCode }).toPath() : collection.url}>
                    <a className="tw-block">
                      <ImageWithFallback
                        record={collection.image}
                        imgField="webpMdUrl"
                        alt={collection.image?.caption || collection.name}
                        className="tw-block tw-static tw-h-56 tw-w-full tw-object-cover tw-mb-8 md:tw-mb-6 tw-border-2 tw-border-solid tw-border-brand-green tw-rounded-lg"
                        fill
                      />
                      <div className="tw-max-w-xs md:tw-max-w-full tw-mx-auto tw-font-styleSans tw-font-semibold tw-text-2xl md:tw-text-xl tw-leading-none tw-mb-5 md:tw-mb-4 tw-text-center">{collection.name}</div>
                      <div className="tw-text-center">{truncate(collection.strippedDescription, { length: 100, omission: '...' })}</div>
                    </a>
                  </Link>
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </div>

      <Homework />

      {countryOrCityRecord ? (
        <RelatedCollections
          currentCollection={collection}
          countryOrCityRecord={countryOrCityRecord}
        />
      ) : null}
    </>
  );
};

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<{ category: Category; collection: Collection }> =
  async (context) => {
    const { id, cateId } = context.params || {};

    if (!id || !cateId) {
      return {
        notFound: true,
        revalidate: RevalidateSettings.short,
      };
    }

    const translation = await prepareTranslations(context, ['pages/collections/[id]/[cateId]']);
    const [collection] = await ApiClient.getCollection(
      { id: String(id), view: 'with_categories' },
      { 'X-NEXT-LOCALE': translation.locale }
    );

    if ('error' in collection) {
      return {
        notFound: true,
        revalidate: RevalidateSettings.short,
      };
    }

    const [category] = await ApiClient.getCategory(
      { id: String(cateId), view: 'with_image' },
      { 'X-NEXT-LOCALE': translation.locale }
    );

    if ('error' in category) {
      return {
        notFound: true,
        revalidate: RevalidateSettings.short,
      };
    }

    return {
      props: {
        collection,
        category,
        ...translation
      },
      revalidate: RevalidateSettings.short,
    };
  };

export default Page;
